import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Alert,
  Typography,
  IconButton,
  FormHelperText,
  Grid,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import axios from '../../utils/axios';

const PlanManagement = () => {
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    duration: '',
    validityPeriod: '',
    status: 'active',
    categories: [],
  });
  const [formErrors, setFormErrors] = useState({});

  const columns = [
    { field: 'name', headerName: t('planName'), flex: 1 },
    {
      field: 'price',
      headerName: t('price'),
      width: 100,
      renderCell: (params) => `${params.value}€`,
    },
    {
      field: 'duration',
      headerName: t('duration'),
      width: 130,
      renderCell: (params) => `${params.value} ${t('minutes')}`,
    },
    {
      field: 'validityPeriod',
      headerName: t('validityPeriod'),
      width: 130,
      renderCell: (params) => `${params.value} ${t('months')}`,
    },
    {
      field: 'categories',
      headerName: t('categories'),
      flex: 1,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          {params.value.map((category) => (
            <Typography key={category} variant="body2">
              {t(category.toLowerCase())}
            </Typography>
          ))}
        </Stack>
      ),
    },
    {
      field: 'status',
      headerName: t('status'),
      width: 120,
      renderCell: (params) => t(params.value.toLowerCase()),
    },
    {
      field: 'actions',
      headerName: t('actions'),
      width: 120,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <IconButton
            size="small"
            onClick={() => handleOpenDialog(params.row)}
            color="primary"
          >
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(params.row._id)}
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        </Stack>
      ),
    },
  ];

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/admin/plans');
      setPlans(response.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching plans:', error);
      setError(t('errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (plan = null) => {
    if (plan) {
      setSelectedPlan(plan);
      setFormData({
        name: plan.name,
        description: plan.description,
        price: plan.price,
        duration: plan.duration,
        validityPeriod: plan.validityPeriod,
        status: plan.status,
        categories: plan.categories,
      });
    } else {
      setSelectedPlan(null);
      setFormData({
        name: '',
        description: '',
        price: '',
        duration: '',
        validityPeriod: '',
        status: 'active',
        categories: [],
      });
    }
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedPlan(null);
    setFormData({
      name: '',
      description: '',
      price: '',
      duration: '',
      validityPeriod: '',
      status: 'active',
      categories: [],
    });
    setFormErrors({});
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name) errors.name = t('required');
    if (!formData.description) errors.description = t('required');
    if (!formData.price || formData.price <= 0) errors.price = t('invalidPrice');
    if (!formData.duration || formData.duration < 30) errors.duration = t('invalidDuration');
    if (!formData.validityPeriod || formData.validityPeriod < 1) errors.validityPeriod = t('required');
    if (!formData.categories.length) errors.categories = t('selectCategory');
    return errors;
  };

  const handleSubmit = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      if (selectedPlan) {
        await axios.put(`/api/admin/plans/${selectedPlan._id}`, formData);
      } else {
        await axios.post('/api/admin/plans', formData);
      }
      handleCloseDialog();
      fetchPlans();
    } catch (error) {
      console.error('Error saving plan:', error);
      setError(t('errorSaving'));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('deleteConfirmation'))) {
      try {
        await axios.delete(`/api/admin/plans/${id}`);
        fetchPlans();
      } catch (error) {
        console.error('Error deleting plan:', error);
        setError(t('errorDeleting'));
      }
    }
  };

  return (
    <Box sx={{ height: 600, width: '100%' }}>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h5" component="h1">
          {t('planManagement')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          {t('addPlan')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <DataGrid
        rows={plans}
        columns={columns}
        pageSize={10}
        rowsPerPageOptions={[10]}
        getRowId={(row) => row._id}
        loading={loading}
        disableSelectionOnClick
      />

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedPlan ? t('editPlan') : t('addPlan')}
        </DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 2 }}>
            <Stack spacing={3}>
              <FormControl fullWidth error={!!formErrors.categories}>
                <InputLabel>{t('categories')}</InputLabel>
                <Select
                  multiple
                  value={formData.categories}
                  onChange={(e) => setFormData({ ...formData, categories: e.target.value })}
                  label={t('categories')}
                >
                  {['Yoga', 'Shiatsu', 'Chi Nei Tsang', 'Soin', 'Ateliers', 'Autres'].map((category) => (
                    <MenuItem key={category} value={category}>
                      {t(category.toLowerCase())}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.categories && (
                  <FormHelperText>{formErrors.categories}</FormHelperText>
                )}
              </FormControl>
              <TextField
                name="name"
                label={t('planName')}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                error={!!formErrors.name}
                helperText={formErrors.name}
                fullWidth
                required
              />
              <TextField
                name="description"
                label={t('description')}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                error={!!formErrors.description}
                helperText={formErrors.description}
                fullWidth
                required
                multiline
                rows={4}
              />
              <TextField
                name="price"
                label={t('price')}
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                error={!!formErrors.price}
                helperText={formErrors.price}
                fullWidth
                required
                type="number"
                InputProps={{
                  endAdornment: '€',
                }}
              />
              <TextField
                name="duration"
                label={t('duration')}
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                error={!!formErrors.duration}
                helperText={formErrors.duration || t('durationHelp')}
                fullWidth
                required
                type="number"
              />
              <TextField
                name="validityPeriod"
                label={t('validityPeriod')}
                value={formData.validityPeriod}
                onChange={(e) => setFormData({ ...formData, validityPeriod: e.target.value })}
                error={!!formErrors.validityPeriod}
                helperText={formErrors.validityPeriod}
                fullWidth
                required
                type="number"
                InputProps={{
                  endAdornment: t('months'),
                }}
              />
              <FormControl fullWidth>
                <InputLabel>{t('status')}</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  label={t('status')}
                >
                  <MenuItem value="active">{t('active')}</MenuItem>
                  <MenuItem value="inactive">{t('inactive')}</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('cancel')}</Button>
          <Button onClick={handleSubmit} variant="contained">
            {selectedPlan ? t('save') : t('create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlanManagement; 